import { SKIPPED_CARD_ERROR_PREFIX } from "@juristic/types/models/chat";
import { UiDataPartType } from "@juristic/types/stream/ai/data_events";
import { ToolHookEventType } from "@juristic/types/stream/ai/tool_hooks";
import { HIDDEN_TOOL_NAMES } from "@juristic/types/stream/ai/tool_metadata";
import { CheckIcon, DocumentIcon, FormatIcon, GlobeIcon, ListLinesIcon, LockIcon, PencilIcon, SearchIcon } from "@UI";
import { getToolName, isToolUIPart } from "ai";
/** Backend tool titles that mutate the document → reviewed via an Apply card. */ export const MUTATING_TOOL_NAMES = new Set([
    "exec",
    "edit_rephrase_selection",
    "insert_content",
    "replace_section",
    "apply_formatting",
    "correct_grammar",
    "translate_document",
    "redact_sensitive_info",
    "standardize_legal_citations",
    "anonymize_document",
    "accept_changes",
    "reject_changes"
]);
/**
 * Tool titles executed on the client against the Word document. Mostly
 * read/discovery, plus `replace_text_document` (a deterministic find/replace
 * under tracked changes) and `add_comment`: both are reviewed in Word, so no Apply card.
 */ export const CLIENT_READ_TOOL_NAMES = new Set([
    "get_selection",
    "get_document_outline",
    "read_document_section",
    "find_in_document",
    "get_all_paragraphs_for_document",
    "list_changes",
    "replace_text_document",
    "add_comment"
]);
export function isMutatingTool(toolName) {
    return MUTATING_TOOL_NAMES.has(toolName);
}
export function isClientReadTool(toolName) {
    return CLIENT_READ_TOOL_NAMES.has(toolName);
}
/** The translated tool name, else `edit_rephrase_selection` → `Edit rephrase selection`. */ export function formatToolName(toolName, t) {
    const name = toolName.replace(/^tool-/, "");
    const words = name.split(/[_\s-]+/).filter(Boolean).join(" ");
    return t(`chat.tools.${name}`, {
        defaultValue: words.charAt(0).toUpperCase() + words.slice(1)
    });
}
const ENDED_TOOL_ERRORS = new Set([
    "Stopped",
    "Interrupted"
]);
/** Map a tool part to a status tone; unfinished steps of a settled message, and stopped, interrupted or skipped calls, are stopped. */ export function toolStatusTone(part, isLive = true) {
    if (part.state === "output-error") {
        const hasEnded = ENDED_TOOL_ERRORS.has(part.errorText) || part.errorText.startsWith(SKIPPED_CARD_ERROR_PREFIX);
        return hasEnded ? "stopped" : "error";
    }
    if (part.state === "output-available") return "done";
    return isLive ? "running" : "stopped";
}
const quoted = (value)=>typeof value === "string" && value.trim() ? `“${value.trim()}”` : undefined;
const text = (value)=>typeof value === "string" && value.trim() ? value.trim() : undefined;
const TOOL_ICONS = {
    get_selection: DocumentIcon,
    get_document_outline: DocumentIcon,
    read_document_section: DocumentIcon,
    get_all_paragraphs_for_document: DocumentIcon,
    list_changes: DocumentIcon,
    find_in_document: SearchIcon,
    replace_text_document: PencilIcon,
    add_comment: PencilIcon,
    edit_rephrase_selection: PencilIcon,
    insert_content: PencilIcon,
    replace_section: PencilIcon,
    correct_grammar: CheckIcon,
    apply_formatting: FormatIcon,
    translate_document: GlobeIcon,
    redact_sensitive_info: LockIcon,
    anonymize_document: LockIcon,
    standardize_legal_citations: ListLinesIcon,
    accept_changes: CheckIcon,
    reject_changes: CheckIcon,
    exec: DocumentIcon,
    exec_write: PencilIcon
};
/** The tool family icon shown in the activity feed. */ export function toolIcon(toolName) {
    return TOOL_ICONS[toolName] ?? ListLinesIcon;
}
const TOOL_TARGETS = {
    read_document_section: (input, output)=>text(input?.header) ?? text(output?.header),
    find_in_document: (input)=>quoted(input?.query),
    replace_text_document: (input)=>quoted(input?.searchText)
};
/** What the step acted on, read from its arguments when they name something. */ export function toolTarget(toolName, input, output) {
    const asArgs = (value)=>value && typeof value === "object" ? value : undefined;
    return TOOL_TARGETS[toolName]?.(asArgs(input), asArgs(output));
}
/** Tools whose only surface is the question dock and its line in the thread. */ export const QUESTION_TOOL_NAMES = [
    "ask_user_choice"
];
const HIDDEN_TOOLS = new Set([
    ...HIDDEN_TOOL_NAMES,
    ...QUESTION_TOOL_NAMES
]);
function isSilentPart(part) {
    if (isToolUIPart(part)) return HIDDEN_TOOLS.has(getToolName(part));
    if (part.type === UiDataPartType.Error) return false;
    if (part.type === UiDataPartType.ToolHook) return part.data.type !== ToolHookEventType.ConfirmationRequest && part.data.type !== ToolHookEventType.ChoiceRequest;
    if (part.type === "text" || part.type === "reasoning") return !part.text;
    return part.type === "step-start" || part.type.startsWith("data-");
}
function finishedReadToolName(part) {
    if (!part || !isToolUIPart(part)) return null;
    const name = getToolName(part);
    return isClientReadTool(name) && toolStatusTone(part) === "done" ? name : null;
}
/** How many finished calls of the same read tool, back to back, this line stands for; 0 when an earlier line counts it. */ export function readToolRunLength(parts, index) {
    const name = finishedReadToolName(parts[index]);
    if (!name) return 1;
    const neighbour = (from, step)=>{
        let i = from + step;
        while(parts[i] && isSilentPart(parts[i]))i += step;
        return i;
    };
    if (finishedReadToolName(parts[neighbour(index, -1)]) === name) return 0;
    let count = 1;
    for(let i = neighbour(index, 1); finishedReadToolName(parts[i]) === name; i = neighbour(i, 1))count++;
    return count;
}
